/**
 * Idle-session policy — FMP-AU-005.
 *
 * "The portal shall expire an idle session after 60 minutes and require
 * re-authentication." Two separate things enforce that here:
 *
 *   1. a sweep in AuthContext that ends the session on its own, so an unattended
 *      screen stops showing fleet data instead of waiting for someone to return;
 *   2. a gate in api.js that refuses to spend the refresh token on a session that
 *      has already gone idle. Without the gate the first rule is cosmetic: the
 *      refresh token is good for days, so the next click would silently revive
 *      the session the sweep had just ended.
 *
 * This module is deliberately framework-free. Both api.js and AuthContext need
 * it, and importing React here would make that a circular dependency.
 *
 * Only genuine user input counts as activity. Network traffic deliberately does
 * NOT: the dashboard re-reads the fleet every 30 s, so counting requests would
 * keep any open tab awake for ever and the timeout would never fire at all.
 */

/**
 * The limit FMP-AU-005 requires. Production must run at 60 — anything longer
 * breaks the requirement outright, and anything shorter signs people out sooner
 * than the specification promises them.
 */
export const DEFAULT_IDLE_LIMIT_MINUTES = 60;

/**
 * REACT_APP_IDLE_LIMIT_MINUTES exists so the timeout can be exercised by hand in
 * a minute or two instead of an hour. Set it in web-portal/.env.development.local,
 * which is gitignored and loaded only by `npm start` — never by `npm run build` —
 * so a short testing value cannot reach a deployed portal. A missing, zero or
 * unparseable value falls back to 60.
 */
export function parseIdleLimitMinutes(raw) {
  const minutes = Number(raw);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : DEFAULT_IDLE_LIMIT_MINUTES;
}

// Referenced directly, with no `typeof process` guard around it. The build
// replaces this exact expression with a literal at compile time, but it does NOT
// define `process` itself, so guarding the read sends the browser down the
// undefined branch and silently restores the default — which is precisely the
// bug this comment exists to prevent a second time.
export const IDLE_LIMIT_MINUTES = parseIdleLimitMinutes(process.env.REACT_APP_IDLE_LIMIT_MINUTES);

export const IDLE_LIMIT_MS = IDLE_LIMIT_MINUTES * 60 * 1000;
export const IDLE_CHECK_MS = 30 * 1000;        // how often the sweep looks

// Loud on purpose: a portal running a non-standard idle limit does not meet
// FMP-AU-005, and that must be obvious in the console rather than discovered in
// a verification run.
if (IDLE_LIMIT_MINUTES !== DEFAULT_IDLE_LIMIT_MINUTES && typeof console !== "undefined") {
  console.warn(
    `[idle] Session idle limit is ${IDLE_LIMIT_MINUTES} min, not the required ${DEFAULT_IDLE_LIMIT_MINUTES} min ` +
    `(FMP-AU-005). This build is for testing only — do not deploy it.`
  );
}

const LAST_ACTIVITY_KEY = "last_activity_at";

// mousemove alone fires hundreds of times a minute; one write per 30 s is enough
// resolution for a 60-minute limit and keeps localStorage quiet.
const WRITE_THROTTLE_MS = 30 * 1000;

/**
 * Events that mean a person is actually there.
 *
 * `scroll` is deliberately absent: it fires for programmatic scrolling too, and
 * this portal re-renders lists on a timer, so a page that moves on its own would
 * keep resetting the clock. `wheel` covers real scrolling by a human.
 *
 * `mousemove` is handled separately in startActivityTracking, because the browser
 * fires it when content moves under a stationary cursor — on this portal the
 * dashboard re-reads the fleet every 30 s, which is frequent enough to hold the
 * clock open for ever on an unattended screen. Only a changed pointer position
 * counts.
 */
const ACTIVITY_EVENTS = ["mousedown", "keydown", "click", "wheel", "touchstart"];

let lastWrite = 0;

/**
 * Wall-clock now, overridable through `window.__IDLE_NOW__`.
 *
 * The override exists so the 59- and 61-minute boundaries can be verified in
 * seconds instead of sitting through an hour, the same way the 60 s fleet-count
 * and 180 s health boundaries are tested. Nothing in the product sets it.
 */
export function now() {
  const override = typeof window !== "undefined" ? window.__IDLE_NOW__ : undefined;
  return typeof override === "number" ? override : Date.now();
}

// Storage throws in a private window or with site data blocked. Losing the
// timestamp must never break sign-in, so every access fails soft.
function readLastActivity() {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeLastActivity(ts) {
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    /* storage unavailable — the sweep simply cannot act */
  }
}

/** Record that the user did something. Throttled unless `force` is set. */
export function markActivity(force = false) {
  const ts = now();
  if (!force && ts - lastWrite < WRITE_THROTTLE_MS) return;
  lastWrite = ts;
  writeLastActivity(ts);
}

/** Start the clock fresh. Called on every successful sign-in. */
export function resetActivity() {
  lastWrite = 0;
  markActivity(true);
}

/** Forget the clock, so the next session starts clean. Called on sign-out. */
export function clearActivity() {
  lastWrite = 0;
  try {
    window.localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Milliseconds since the last recorded activity, or null when none is recorded. */
export function idleMs() {
  const last = readLastActivity();
  return last === null ? null : Math.max(0, now() - last);
}

/**
 * Whether the session has been idle at or beyond the limit.
 *
 * No recorded activity reads as "not idle" on purpose: a session restored before
 * this module ever ran must not be killed on a missing timestamp. The sweep
 * writes one as soon as it starts, so the gap closes within a tick.
 */
export function isIdleExpired() {
  const ms = idleMs();
  return ms !== null && ms >= IDLE_LIMIT_MS;
}

/**
 * Listen for user activity. Returns the cleanup function.
 *
 * Listeners are passive — none of them calls preventDefault, and marking them so
 * keeps scrolling smooth.
 */
export function startActivityTracking() {
  if (typeof window === "undefined") return () => {};

  const onActivity = () => markActivity();
  ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

  // A stationary cursor with the page moving underneath it is not a person at
  // the keyboard, so require the pointer to have actually moved.
  let lastX = null;
  let lastY = null;
  const onMouseMove = (event) => {
    if (event.clientX === lastX && event.clientY === lastY) return;
    lastX = event.clientX;
    lastY = event.clientY;
    markActivity();
  };
  window.addEventListener("mousemove", onMouseMove, { passive: true });

  return () => {
    ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
    window.removeEventListener("mousemove", onMouseMove);
  };
}

/**
 * Current idle state, for checking behaviour from the browser console:
 *   __idleDebug()
 * Attached below in development builds only.
 */
export function idleDebug() {
  const ms = idleMs();
  return {
    limitMinutes: IDLE_LIMIT_MINUTES,
    idleSeconds: ms === null ? null : Math.round(ms / 1000),
    secondsUntilSignOut: ms === null ? null : Math.round((IDLE_LIMIT_MS - ms) / 1000),
    expired: isIdleExpired(),
    lastActivity: (() => {
      const last = readLastActivity();
      return last === null ? null : new Date(last).toISOString();
    })(),
  };
}

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__idleDebug = idleDebug;
}
